import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';

const translations = {
  nb: {
    title: 'Fyll ut skjemaet',
    description: 'Fyll ut skjemaet til høyre eller bruk kontaktinformasjonen nedenfor, så vil vi ta kontakt med deg innen kort tid for å sette opp et møte for å diskutere deres behov.',
    contact: {
      email: 'post@spacefinder.no',
      phone: '+47 994 26 996',
      linkedin: 'Følg oss på LinkedIn'
    },
    form: {
      fullName: {
        label: 'Fullt navn',
        placeholder: 'Ditt fulle navn'
      },
      company: {
        label: 'Bedrift',
        placeholder: 'Søk etter bedrift',
        searching: 'Søker...'
      },
      email: {
        label: 'E-post',
        placeholder: 'Din e-postadresse'
      },
      phone: {
        label: 'Telefon',
        placeholder: 'Ditt telefonnummer'
      },
      message: {
        label: 'Din henvendelse',
        placeholder: 'Gi oss en beskrivelse om hva henvendelsen gjelder ...'
      },
      required: '*',
      submit: 'Send melding'
    }
  },
  en: {
    title: 'Fill out the form',
    description: 'Fill out the form on the right or use the contact information below, and we will contact you shortly to set up a meeting to discuss your needs.',
    contact: {
      email: 'post@spacefinder.no',
      phone: '+47 994 26 996',
      linkedin: 'Follow us on LinkedIn'
    },
    form: {
      fullName: {
        label: 'Full Name',
        placeholder: 'Your full name'
      },
      company: {
        label: 'Company',
        placeholder: 'Search for company',
        searching: 'Searching...'
      },
      email: {
        label: 'Email',
        placeholder: 'Your email address'
      },
      phone: {
        label: 'Phone',
        placeholder: 'Your phone number'
      },
      message: {
        label: 'Your inquiry',
        placeholder: 'Give us a description of your inquiry ...'
      },
      required: '*',
      submit: 'Send message'
    }
  }
};

export default function FormSection() {
  const router = useRouter();
  const t = translations[router.locale || 'nb'];
  const dropdownRef = useRef(null);
  const searchTimeout = useRef(null);

  const [formData, setFormData] = useState({
    fullName: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [organizationNumber, setOrganizationNumber] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    // Handle clicking outside of dropdown
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchCompanies = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(`https://data.brreg.no/enhetsregisteret/api/enheter?navn=${encodeURIComponent(query)}&size=5`);
      const data = await response.json();
      setSearchResults(data._embedded?.enheter || []);
      setShowDropdown(true);
    } catch (error) {
      console.error('Error searching companies:', error);
      setSearchResults([]);
    }
    setIsSearching(false);
  };

  const handleCompanySearch = (e) => {
    const query = e.target.value;
    setFormData(prev => ({ ...prev, company: query }));
    
    // Clear previous timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Set new timeout for search
    searchTimeout.current = setTimeout(() => {
      searchCompanies(query);
    }, 300);
  };

  const handleCompanySelect = (company) => {
    setFormData(prev => ({ ...prev, company: company.navn }));
    setOrganizationNumber(company.organisasjonsnummer);
    setShowDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);

    try {
      // Prepare data for GravityForms and Make.com webhook
      const requestData = {
        fullName: formData.fullName,
        companyName: formData.company,
        email: formData.email,
        phoneNumber: formData.phone,
        message: formData.message,
        organizationNumber: organizationNumber,
        formId: 1 // Specify form ID 1
      };

      // Send data to GravityForms endpoint
      const response = await fetch('/api/gravity-forms-submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to send request');
      }

      // Set success state and reset form
      setSubmitSuccess(true);
      setFormData({
        fullName: '',
        company: '',
        email: '',
        phone: '',
        message: ''
      });
      setOrganizationNumber('');
      
      // Update URL with hash
      router.push('/kontakt-sendt');
      
      // Reset success state after 3 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 3000);

    } catch (error) {
      console.error('Error in form submission:', error);
      setSubmitError('Det oppstod en feil. Vennligst prøv igjen senere.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  return (
    <section className="grid md:grid-cols-2 gap-16 py-20">
      <div>
        <h2 className="text-5xl font-bold mb-6">{t.title}</h2>
        <p className="text-xl mb-8">{t.description}</p>
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center">
              <svg className="w-6 h-6 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
                <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
              </svg>
            </div>
            <a href="mailto:post@spacefinder.no" className="text-xl">{t.contact.email}</a>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center">
              <svg className="w-6 h-6 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                <path fillRule="evenodd" d="M1.5 4.5a3 3 0 013-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 01-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 006.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 011.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 01-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5z" clipRule="evenodd" />
              </svg>
            </div>
            <a href="tel:+4799426996" className="text-xl">{t.contact.phone}</a>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center">
              <svg className="w-6 h-6 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
              </svg>
            </div>
            <a href="https://www.linkedin.com/company/scout-eiendom" target="_blank" rel="noopener noreferrer" className="text-xl">
              {t.contact.linkedin}
            </a>
          </div>
        </div>
      </div>

      <div className="bg-info-sections p-8 rounded-3xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="fullName" className="block mb-2">
                {t.form.fullName.label} <span className="text-red-500">{t.form.required}</span>
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                className="w-full p-3 rounded-lg bg-gray-50"
                placeholder={t.form.fullName.placeholder}
                required
              />
            </div>
            <div className="relative" ref={dropdownRef}>
              <label htmlFor="company" className="block mb-2">
                {t.form.company.label}
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleCompanySearch}
                className="w-full p-3 rounded-lg bg-gray-50"
                placeholder={t.form.company.placeholder}
                autoComplete="off"
              />
              {showDropdown && searchResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg">
                  {searchResults.map((company) => (
                    <div
                      key={company.organisasjonsnummer}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100 border-b last:border-b-0"
                      onClick={() => handleCompanySelect(company)}
                    >
                      <div className="font-medium company-search-result">{company.navn}</div>
                      <div className="text-sm company-search-result">Org.nr: {company.organisasjonsnummer}</div>
                    </div>
                  ))}
                </div>
              )}
              {isSearching && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg px-4 py-2 text-gray-600">
                  {t.form.company.searching}
                </div>
              )}
              <input
                type="hidden"
                name="organizationNumber"
                value={organizationNumber}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="email" className="block mb-2">
                {t.form.email.label} <span className="text-red-500">{t.form.required}</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-3 rounded-lg bg-gray-50"
                placeholder={t.form.email.placeholder}
                required
              />
            </div>
            <div>
              <label htmlFor="phone" className="block mb-2">
                {t.form.phone.label}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full p-3 rounded-lg bg-gray-50"
                placeholder={t.form.phone.placeholder}
              />
            </div>
          </div>

          <div>
            <label htmlFor="message" className="block mb-2">
              {t.form.message.label} <span className="text-red-500">{t.form.required}</span>
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              className="w-full p-3 rounded-lg bg-gray-50 dark:text-gray-900 h-32"
              placeholder={t.form.message.placeholder}
              required
            />
          </div>

          <div>
            <button
              type="submit"
              className="w-full button-base button-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sender...' : t.form.submit}
            </button>
            {submitError && (
              <div className="text-red-600 mt-4 text-center">
                {submitError}
              </div>
            )}
            {submitSuccess && (
              <div className="text-green-600 mt-4 text-center">
                <div className="mb-2">
                  <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p>Takk for din henvendelse! Vi vil kontakte deg så snart som mulig.</p>
              </div>
            )}
          </div>
        </form>
      </div>
    </section>
  );
} 